import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Container, Text, Button, Flex, VStack, 
  Input, FormControl, FormLabel, Select, useToast, HStack,
  IconButton, Divider, useBreakpointValue, InputGroup, InputLeftAddon,
  Progress, Alert, AlertIcon, FormErrorMessage, Checkbox
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, CheckIcon, InfoIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const MotionBox = motion(Box);
const MotionButton = motion(Button);

// Add global styles for autofill on mobile
const globalStyles = `
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: inset 0 0 0px 1000px white !important;
    -webkit-text-fill-color: black !important;
    background-color: white !important;
    color: black !important;
    caret-color: black !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

// Inject global styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = globalStyles;
  if (!document.head.querySelector('[data-autofill-address-styles]')) {
    styleElement.setAttribute('data-autofill-address-styles', 'true');
    document.head.appendChild(styleElement);
  }
}

// Indian states for dropdown
const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const AddressManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [loading, setLoading] = useState(false);
  const [validatingPincode, setValidatingPincode] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [formData, setFormData] = useState({
    deliveringTo: '',
    addressLine1: '',
    addressLine2: '',
    landmark: '',
    locality: '',
    city: '',
    state: '',
    pincode: '',
    phoneNumber: '',
    isDefault: false
  });

  const isEditing = location.state?.mode === 'edit';
  const editingProfile = location.state?.profile;

  useEffect(() => {
    if (isEditing && editingProfile) {
      // Populate form with existing profile data
      setFormData({
        deliveringTo: editingProfile.deliveringTo || location.state?.fullName || '',
        addressLine1: editingProfile.addressLine1 || '',
        addressLine2: editingProfile.addressLine2 || '',
        landmark: editingProfile.landmark || '',
        locality: editingProfile.locality || '',
        city: editingProfile.city || '',
        state: editingProfile.state || '',
        pincode: editingProfile.pincode || '',
        phoneNumber: editingProfile.phoneNumber || '',
        isDefault: editingProfile.isDefault || false
      });
    } else if (!isEditing && location.state?.fullName) {
      // Pre-fill name for new profile
      setFormData(prev => ({
        ...prev,
        deliveringTo: location.state.fullName
      }));
    }
  }, [isEditing, editingProfile, location.state]);

  // Calculate completion percentage
  useEffect(() => {
    const requiredFields = ['deliveringTo', 'addressLine1', 'locality', 'city', 'state', 'pincode', 'phoneNumber'];
    const filledFields = requiredFields.filter(field => formData[field].trim() !== '');
    const percentage = Math.round((filledFields.length / requiredFields.length) * 100);
    setCompletionPercentage(percentage);
  }, [formData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        if (completionPercentage === 100 || isEditing) {
          handleSave();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [completionPercentage, isEditing]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhoneChange = (value) => {
    // Remove any non-digit characters and limit to 10 digits
    const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
    handleInputChange('phoneNumber', cleanedValue);
  };

  const handlePincodeChange = async (value) => {
    // Remove any non-digit characters and limit to 6 digits
    const cleanedValue = value.replace(/\D/g, '').slice(0, 6);
    handleInputChange('pincode', cleanedValue);

    // Auto-fetch city and state for 6-digit pincode
    if (cleanedValue.length === 6) {
      setValidatingPincode(true);
      try {
        // This is a mock API call - replace with actual pincode API
        const response = await fetch(`https://api.postalpincode.in/pincode/${cleanedValue}`);
        const data = await response.json();
        
        if (data[0].Status === 'Success' && data[0].PostOffice.length > 0) {
          const postOffice = data[0].PostOffice[0];
          setFormData(prev => ({
            ...prev,
            city: postOffice.District,
            state: postOffice.State,
            pincode: cleanedValue
          }));
          toast({
            title: 'Pincode Verified',
            description: `City and state auto-filled for ${cleanedValue}`,
            status: 'success',
            duration: 2000,
            isClosable: true
          });
        }
      } catch (error) {
        // Silently fail - user can still enter manually
      } finally {
        setValidatingPincode(false);
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    const required = ['deliveringTo', 'addressLine1', 'locality', 'city', 'state', 'pincode', 'phoneNumber'];
    
    required.forEach(field => {
      if (!formData[field].trim()) {
        errors[field] = 'This field is required';
      }
    });

    if (formData.phoneNumber && formData.phoneNumber.length !== 10) {
      errors.phoneNumber = 'Phone number must be 10 digits';
    }

    if (formData.pincode && formData.pincode.length !== 6) {
      errors.pincode = 'Pincode must be 6 digits';
    }

    if (formData.deliveringTo && formData.deliveringTo.length < 2) {
      errors.deliveringTo = 'Name must be at least 2 characters';
    }

    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      toast({
        title: 'Please fix the errors',
        description: 'Check the highlighted fields below',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const profileData = {
        deliveringTo: formData.deliveringTo,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        landmark: formData.landmark,
        locality: formData.locality,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        phoneNumber: formData.phoneNumber,
        isDefault: formData.isDefault
      };

      let response;
      if (isEditing) {
        response = await axios.put(`/api/auth/profile/edit/${editingProfile.id}/`, profileData);
        toast({
          title: 'Address Updated',
          description: 'Your address has been updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } else {
        response = await axios.post('/api/auth/profile/create/', profileData);
        toast({
          title: 'Address Added',
          description: 'Your new address has been saved successfully',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      }

      // Navigate back to checkout with the new/updated profile
      navigate('/checkout', { 
        state: { 
          from: 'address-management',
          newProfile: response.data,
          isNew: !isEditing
        } 
      });
    } catch (error) {
      // Handle different types of errors
      let errorMessage = 'Failed to save address. Please try again.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle field-specific errors
        if (typeof errorData === 'object' && !errorData.detail) {
          const fieldErrors = Object.entries(errorData).map(([field, messages]) => {
            const fieldName = field === 'deliveringTo' ? 'Name' : 
                             field === 'addressLine1' ? 'Address Line 1' :
                             field === 'phoneNumber' ? 'Phone Number' :
                             field === 'pincode' ? 'Pincode' : field;
            return `${fieldName}: ${Array.isArray(messages) ? messages[0] : messages}`;
          });
          errorMessage = fieldErrors.join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.non_field_errors) {
          errorMessage = Array.isArray(errorData.non_field_errors) ? 
                        errorData.non_field_errors[0] : errorData.non_field_errors;
        }
      }

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MotionBox 
      bgImage={`url(${process.env.REACT_APP_BG_URL})`}
      minH="100vh" 
      py={{ base: 4, md: 8 }}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Container maxW="container.md" px={{ base: 4, md: 8 }}>
        {/* Header */}
        <Flex align="center" mb={6}>
          <IconButton
            icon={<ChevronLeftIcon />}
            variant="ghost"
            size="lg"
            onClick={() => navigate('/checkout')}
            aria-label="Go back"
            mr={2}
            _hover={{ bg: "gray.100" }}
          />
          <VStack align="start" spacing={1} flex={1}>
            <Heading 
              size={isMobile ? "lg" : "xl"}
              fontFamily="'Cormorant', serif"
              fontWeight="700"
            >
              {isEditing ? 'Edit Address' : 'Add New Address'}
            </Heading>
            {!isEditing && (
              <Flex align="center" w="full" mt={2}>
                <Text fontSize="sm" color="gray.600" fontFamily="'Zain', sans-serif" fontWeight="500" mr={3}>
                  {completionPercentage}% Complete
                </Text>
                <Progress 
                  value={completionPercentage} 
                  size="sm" 
                  colorScheme="blackAlpha"
                  flex={1}
                  borderRadius="full"
                  bg="gray.200"
                  sx={{
                    '& > div': {
                      background: '#45CC2D'
                    }
                  }}
                />
              </Flex>
            )}
          </VStack>
        </Flex>

        {/* Info Alert */}
        <Alert status="info" borderRadius="lg" mb={6} bg="teal.900" color="white"  >
          <AlertIcon as={InfoIcon} color="teal.200"/>
          <Text fontSize="sm" fontFamily="'Zain', sans-serif" fontWeight="500">
            Fill in your delivery details. We'll use this information to ensure smooth delivery of your order.
          </Text>
        </Alert>

        {/* Form */}
        <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="xl" boxShadow="md">
          <VStack spacing={5} align="stretch">
            
            {/* Delivering To */}
            <FormControl isRequired isInvalid={formErrors.deliveringTo}>
              <FormLabel fontSize="md" fontWeight="600" color="gray.700" fontFamily="'Zain', sans-serif">
                Delivering To
              </FormLabel>
              <Input
                placeholder="Full Name (as per government ID)"
                value={formData.deliveringTo}
                onChange={(e) => handleInputChange('deliveringTo', e.target.value)}
                focusBorderColor="black"
                size={isMobile ? "md" : "lg"}
              />
              <FormErrorMessage>{formErrors.deliveringTo}</FormErrorMessage>
            </FormControl>

            {/* Address Lines */}
            <VStack spacing={4} align="stretch">
              <FormControl isRequired isInvalid={formErrors.addressLine1}>
                <FormLabel fontSize="md" fontWeight="600" color="gray.700" fontFamily="'Zain', sans-serif">
                  Address Line 1
                </FormLabel>
                <Input
                  placeholder="House/Flat No., Building Name"
                  value={formData.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                  focusBorderColor="black"
                  size={isMobile ? "md" : "lg"}
                />
                <FormErrorMessage>{formErrors.addressLine1}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="md" fontWeight="600" color="gray.700" fontFamily="'Zain', sans-serif">
                  Address Line 2 (Optional)
                </FormLabel>
                <Input
                  placeholder="Road Name, Area"
                  value={formData.addressLine2}
                  onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  focusBorderColor="black"
                  size={isMobile ? "md" : "lg"}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="md" fontWeight="600" color="gray.700" fontFamily="'Zain', sans-serif">
                  Landmark (Optional)
                </FormLabel>
                <Input
                  placeholder="Near famous place, mall, or monument"
                  value={formData.landmark}
                  onChange={(e) => handleInputChange('landmark', e.target.value)}
                  focusBorderColor="black"
                  size={isMobile ? "md" : "lg"}
                />
              </FormControl>
            </VStack>

            <Divider />

            {/* Location Details */}
            <VStack spacing={4} align="stretch">
              <FormControl isRequired isInvalid={formErrors.locality}>
                <FormLabel fontSize="md" fontWeight="600" color="gray.700" fontFamily="'Zain', sans-serif">
                  Locality
                </FormLabel>
                <Input
                  placeholder="Locality/Neighbourhood"
                  value={formData.locality}
                  onChange={(e) => handleInputChange('locality', e.target.value)}
                  focusBorderColor="black"
                  size={isMobile ? "md" : "lg"}
                />
                <FormErrorMessage>{formErrors.locality}</FormErrorMessage>
              </FormControl>

              <HStack spacing={4}>
                <FormControl isRequired isInvalid={formErrors.pincode}>
                  <FormLabel fontSize="md" fontWeight="600" color="gray.700" fontFamily="'Zain', sans-serif">
                    Pincode
                  </FormLabel>
                  <Input
                    placeholder="123456"
                    value={formData.pincode}
                    onChange={(e) => handlePincodeChange(e.target.value)}
                    focusBorderColor="black"
                    size={isMobile ? "md" : "lg"}
                    maxLength={6}
                    isDisabled={validatingPincode}
                  />
                  <FormErrorMessage>{formErrors.pincode}</FormErrorMessage>
                  {validatingPincode && (
                    <Text fontSize="xs" color="blue.500" mt={1}>
                      Fetching location details...
                    </Text>
                  )}
                </FormControl>

                <FormControl isRequired isInvalid={formErrors.city}>
                  <FormLabel fontSize="md" fontWeight="600" color="gray.700" fontFamily="'Zain', sans-serif">
                    City
                  </FormLabel>
                  <Input
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    focusBorderColor="black"
                    size={isMobile ? "md" : "lg"}
                  />
                  <FormErrorMessage>{formErrors.city}</FormErrorMessage>
                </FormControl>
              </HStack>

              <FormControl isRequired isInvalid={formErrors.state}>
                <FormLabel fontSize="md" fontWeight="600" color="gray.700" fontFamily="'Zain', sans-serif">
                  State
                </FormLabel>
                <Select
                  placeholder="Select State"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  focusBorderColor="black"
                  size={isMobile ? "md" : "lg"}
                >
                  {indianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </Select>
                <FormErrorMessage>{formErrors.state}</FormErrorMessage>
              </FormControl>
            </VStack>

            <Divider />

            {/* Phone Number */}
            <FormControl isRequired isInvalid={formErrors.phoneNumber}>
              <FormLabel fontSize="md" fontWeight="600" color="gray.700" fontFamily="'Zain', sans-serif">
                Phone Number
              </FormLabel>
              <InputGroup size={isMobile ? "md" : "lg"}>
                <InputLeftAddon children="+91" />
                <Input
                  placeholder="9876543210"
                  value={formData.phoneNumber}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  focusBorderColor="black"
                  maxLength={10}
                  type="tel"
                />
              </InputGroup>
              <FormErrorMessage>{formErrors.phoneNumber}</FormErrorMessage>
              <Text fontSize="xs" color="gray.500" mt={1} fontFamily="'Zain', sans-serif" fontWeight="500">
                We'll send delivery updates and OTP on this number
              </Text>
            </FormControl>

            {/* Set as Default Checkbox */}
            <FormControl>
              <Checkbox
                isChecked={formData.isDefault}
                onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                colorScheme="blackAlpha"
                size="md"
              >
                <Text color="gray.700" fontSize="sm" fontFamily="'Zain', sans-serif" fontWeight="500">Set as default address</Text>
              </Checkbox>
              <Text fontSize="xs" color="gray.500" mt={1} fontFamily="'Zain', sans-serif" fontWeight="500">
                This address will be selected automatically for future orders
              </Text>
            </FormControl>

          </VStack>
        </Box>

        {/* Action Buttons */}
        <VStack spacing={3} mt={6}>
          <MotionButton
            bg="black"
            color="white"
            size="lg"
            w="full"
            leftIcon={<CheckIcon />}
            onClick={handleSave}
            isLoading={loading}
            loadingText={isEditing ? "Updating..." : "Saving..."}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            isDisabled={completionPercentage < 100 && !isEditing}
            _hover={{ bg: "gray.800" }}
            fontWeight="600"
            fontFamily="'Zain', sans-serif"
            borderRadius="lg"
            py={6}
          >
            {isEditing ? 'Update Address' : 'Save Address'}
          </MotionButton>
          
          {!isEditing && completionPercentage < 100 && (
            <Text fontSize="sm" color="gray.500" textAlign="center" fontFamily="'Zain', sans-serif" fontWeight="500">
              Complete all required fields to save address
            </Text>
          )}
          
          <Button
            variant="ghost"
            size="lg"
            w="full"
            onClick={() => navigate('/checkout')}
            isDisabled={loading}
            _hover={{ bg: "gray.100" }}
            borderRadius="lg"
            fontFamily="'Zain', sans-serif"
            fontWeight="600"
          >
            Cancel
          </Button>

          {/* Save Shortcut Hint */}
          {completionPercentage === 100 && (
            <Text fontSize="xs" color="gray.400" textAlign="center" fontFamily="'Zain', sans-serif" fontWeight="500">
              💡 Tip: Press Ctrl+Enter to save quickly
            </Text>
          )}
        </VStack>
      </Container>
    </MotionBox>
  );
};

export default AddressManagementPage;
